import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FolderOpen, X } from "lucide-react";
import { cn } from "../../utils/cn";
import { Project, User } from "../../types";
import { UserMultiSelect } from "../ui/UserMultiSelect";
import { useAuth } from "../../hooks/useAuth";

interface AddProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (projectData: any) => void;
    project?: Project | null;
    users?: User[];
}

export function AddProjectModal({ isOpen, onClose, onAdd, project = null, users = [] }: AddProjectModalProps) {
    const { isAdmin } = useAuth();
    const isEditMode = !!project;
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        owner: "",
        pm: [] as string[],
        status: "active",
        priority: "medium",
    });

    // Prefill form when editing
    useEffect(() => {
        if (project) {
            setFormData({
                name: project.name || "",
                description: project.description || "",
                owner: project.owner || "",
                pm: Array.isArray(project.pm) ? project.pm : (project.pm ? [project.pm] : []),
                status: project.status || "active",
                priority: project.priority || "medium",
            });
        } else {
            setFormData({ name: "", description: "", owner: "", pm: [], status: "active", priority: "medium" });
        }
    }, [project]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        onAdd({ ...formData, id: project?.id });
        setFormData({ name: "", description: "", owner: "", pm: [], status: "active", priority: "medium" });
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                        onClick={onClose}
                    />
                    {/* Modal Container */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
                        >
                            {/* Header - Gradient */}
                            <div className="px-6 py-5 bg-gradient-to-r from-siemens-petrol to-siemens-deep-blue flex items-center justify-between">
                                <h2 className="text-lg font-bold text-white flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                        <FolderOpen className="w-5 h-5" />
                                    </div>
                                    {isEditMode ? "Edit Project" : "Create New Project"}
                                </h2>
                                <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/20 transition-colors">
                                    <X className="w-5 h-5 text-white" />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                                {/* Project Name */}
                                <div className="space-y-2">
                                    <label className="flex items-center gap-1 text-sm font-bold text-slate-700 dark:text-slate-300">
                                        Project Name
                                        <span className="text-siemens-petrol">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter project name..."
                                        className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-siemens-petrol/30 focus:border-siemens-petrol transition-all"
                                        autoFocus
                                    />
                                </div>

                                {/* Description */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Brief description of the project..."
                                        rows={3}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-siemens-petrol/30 focus:border-siemens-petrol transition-all resize-none"
                                    />
                                </div>

                                {/* Project Managers - Multi-Select */}
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                            Project Managers
                                        </label>
                                        {!isAdmin && isEditMode && (
                                            <span className="text-xs text-slate-500 italic">Only admins can change PMs</span>
                                        )}
                                    </div>
                                    <UserMultiSelect
                                        label=""
                                        selectedUserIds={formData.pm}
                                        users={users}
                                        onChange={(ids) => setFormData(prev => ({ ...prev, pm: ids }))}
                                        placeholder={!isAdmin && isEditMode ? "Contact an admin to change managers" : "Select project managers..."}
                                        disabled={!isAdmin && isEditMode}
                                    />
                                </div>

                                {/* Status & Priority */}
                                <div className="grid grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                            Status
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {[
                                                { value: "active", label: "Active", gradient: "from-emerald-500 to-green-600" },
                                                { value: "on-hold", label: "On Hold", gradient: "from-amber-500 to-orange-600" },
                                                { value: "completed", label: "Done", gradient: "from-blue-500 to-indigo-600" },
                                            ].map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, status: opt.value }))}
                                                    className={cn(
                                                        "h-9 px-4 rounded-lg text-xs font-bold transition-all",
                                                        formData.status === opt.value
                                                            ? `bg-gradient-to-r ${opt.gradient} text-white shadow-md`
                                                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                                    )}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                            Priority
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {[
                                                { value: "critical", label: "Critical", color: "text-red-600 bg-red-50 dark:bg-red-900/30" },
                                                { value: "high", label: "High", color: "text-orange-600 bg-orange-50 dark:bg-orange-900/30" },
                                                { value: "medium", label: "Medium", color: "text-blue-600 bg-blue-50 dark:bg-blue-900/30" },
                                                { value: "low", label: "Low", color: "text-slate-600 bg-slate-100 dark:bg-slate-800" },
                                            ].map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, priority: opt.value }))}
                                                    className={cn(
                                                        "h-9 px-4 rounded-lg text-xs font-bold transition-all",
                                                        formData.priority === opt.value
                                                            ? `${opt.color} ring-2 ring-offset-1 ring-current`
                                                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                                    )}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 h-12 px-6 text-sm font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!formData.name.trim()}
                                        className={cn(
                                            "flex-1 h-12 px-6 text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2",
                                            formData.name.trim()
                                                ? "bg-gradient-to-r from-siemens-petrol to-siemens-deep-blue text-white hover:shadow-lg hover:shadow-siemens-petrol/30 hover:-translate-y-0.5"
                                                : "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                                        )}
                                    >
                                        <FolderOpen className="w-4 h-4" />
                                        {isEditMode ? "Save Changes" : "Create Project"}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default AddProjectModal;
